import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, qs } from './client.ts';
import type {
  ChangeBatch, Chronology, DescNode, Me, Member, Invitation, Pedigree, PersonDetail, PersonLite, RelativeItem, SearchResult, Stats,
  TimelineItem, TreeDetail,
} from './types.ts';

export const keys = {
  me: ['me'] as const,
  tree: (t: string) => ['tree', t] as const,
  person: (t: string, p: string) => ['tree', t, 'person', p] as const,
  pedigree: (t: string, p: string, g: number) => ['tree', t, 'pedigree', p, g] as const,
  descendants: (t: string, p: string, g: number) => ['tree', t, 'descendants', p, g] as const,
  search: (t: string, params: object) => ['tree', t, 'search', params] as const,
  relatives: (t: string, p: string, kind: string, degree: number) => ['tree', t, 'relatives', p, kind, degree] as const,
  stats: (t: string) => ['tree', t, 'stats'] as const,
  timeline: (t: string, params: object) => ['tree', t, 'timeline', params] as const,
  chronology: (t: string, p: string) => ['tree', t, 'person', p, 'chronology'] as const,
  changes: (t: string, params: object) => ['tree', t, 'changes', params] as const,
  members: (t: string) => ['tree', t, 'members'] as const,
  invitations: (t: string) => ['tree', t, 'invitations'] as const,
  claims: (t: string) => ['tree', t, 'claims'] as const,
  events: (t: string) => ['tree', t, 'family-events'] as const,
  event: (t: string, id: string) => ['tree', t, 'family-events', id] as const,
  newMembers: (t: string) => ['tree', t, 'members', 'new'] as const,
};

export const treeUrl = (t: string) => `/api/trees/${t}`;

export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: async () => {
      try {
        return await api.get<Me>('/api/auth/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });
}

export function useTree(treeId: string | undefined) {
  return useQuery({ queryKey: keys.tree(treeId!), queryFn: () => api.get<TreeDetail>(treeUrl(treeId!)), enabled: !!treeId });
}

export function usePerson(treeId: string, personId: string | null | undefined) {
  return useQuery({
    queryKey: keys.person(treeId, personId!),
    queryFn: () => api.get<PersonDetail>(`${treeUrl(treeId)}/persons/${personId}`),
    enabled: !!personId,
  });
}

export function usePedigree(treeId: string, personId: string | null | undefined, generations: number) {
  return useQuery({
    queryKey: keys.pedigree(treeId, personId!, generations),
    queryFn: () => api.get<Pedigree>(`${treeUrl(treeId)}/persons/${personId}/pedigree${qs({ generations })}`),
    enabled: !!personId,
    placeholderData: keepPreviousData,
  });
}

export function useDescendants(treeId: string, personId: string | null | undefined, generations: number) {
  return useQuery({
    queryKey: keys.descendants(treeId, personId!, generations),
    queryFn: () => api.get<DescNode>(`${treeUrl(treeId)}/persons/${personId}/descendants${qs({ generations })}`),
    enabled: !!personId,
    placeholderData: keepPreviousData,
  });
}

export interface SearchParams {
  q?: string;
  yearFrom?: number;
  yearTo?: number;
  yearField?: 'birth' | 'death';
  place?: string;
  sex?: string;
  living?: string;
  limit?: number;
  offset?: number;
}

export function useSearch(treeId: string, params: SearchParams, enabled = true) {
  return useQuery({
    queryKey: keys.search(treeId, params),
    queryFn: () => api.get<SearchResult>(`${treeUrl(treeId)}/search${qs({ ...params })}`),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useRelatives(treeId: string, personId: string | null | undefined, kind: string, degree = 1) {
  return useQuery({
    queryKey: keys.relatives(treeId, personId!, kind, degree),
    queryFn: () => api.get<{ items: RelativeItem[] }>(`${treeUrl(treeId)}/persons/${personId}/relatives${qs({ kind, degree })}`),
    enabled: !!personId,
  });
}

export function useStats(treeId: string) {
  return useQuery({ queryKey: keys.stats(treeId), queryFn: () => api.get<Stats>(`${treeUrl(treeId)}/stats`) });
}

export function useTimeline(treeId: string, params: { personId?: string; scope?: string; from?: number; to?: number }) {
  return useQuery({
    queryKey: keys.timeline(treeId, params),
    queryFn: () => api.get<{ items: TimelineItem[] }>(`${treeUrl(treeId)}/timeline${qs(params)}`),
    placeholderData: keepPreviousData,
  });
}

export function useChronology(treeId: string, personId: string | undefined) {
  return useQuery({
    queryKey: keys.chronology(treeId, personId!),
    queryFn: () => api.get<Chronology>(`${treeUrl(treeId)}/persons/${personId}/chronology`),
    enabled: !!personId,
  });
}

export function useMembers(treeId: string) {
  return useQuery({ queryKey: keys.members(treeId), queryFn: () => api.get<{ items: Member[] }>(`${treeUrl(treeId)}/members`) });
}

export function useInvitations(treeId: string) {
  return useQuery({ queryKey: keys.invitations(treeId), queryFn: () => api.get<{ items: Invitation[] }>(`${treeUrl(treeId)}/invitations`) });
}

export function useChangesPage(treeId: string, params: { personId?: string; userId?: string; cursor?: string | null }) {
  return useQuery({
    queryKey: keys.changes(treeId, params),
    queryFn: () => api.get<{ items: ChangeBatch[]; nextCursor: string | null }>(`${treeUrl(treeId)}/changes${qs({ ...params, cursor: params.cursor ?? undefined })}`),
  });
}

/** Invalida todo lo que depende del contenido del árbol tras una edición. */
export function useInvalidateTree() {
  const qc = useQueryClient();
  return (treeId: string) => qc.invalidateQueries({ queryKey: ['tree', treeId] });
}

/** Mutación genérica que invalida el árbol al terminar. */
export function useTreeMutation<TVars, TResult = unknown>(treeId: string, fn: (vars: TVars) => Promise<TResult>) {
  const invalidate = useInvalidateTree();
  return useMutation({ mutationFn: fn, onSuccess: () => invalidate(treeId) });
}

export function docUrl(treeId: string, docId: string, variant: 'file' | 'thumb' = 'thumb') {
  return `${treeUrl(treeId)}/documents/${docId}/${variant}`;
}

export type { PersonLite };
